import styled from "styled-components";
import { device } from "../../utils/device";
import { Button, Modal, useMediaQuery, useTheme } from "@mui/material";
import TextView from "../TextView";
import useScreenSize from "../../hooks/useScreenSize";
import { ASSETS } from "../../utils/config";
import { Cancel } from "../../assets/icons";

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  max-height: 90vh;
  overflow-y: auto;
  margin: 16px;

  /* Mobile Portrait */
  @media ${device.mobilePortrait} {
    width: 90vw;
    min-height: auto;
    padding: 20px 16px;
    max-width: none;
  }

  /* Mobile Landscape */
  @media ${device.mobileLandscape} {
    width: 90vw;
    max-width: 480px;
    min-height: auto;
  }

  /* Tablets */
  @media ${device.sm} {
    width: 90vw;
    max-width: 550px;
    padding: 24px;
  }

  /* Desktop and larger screens */
  @media ${device.md} {
    width: 70vw;
    max-width: 600px;
    padding: 28px;
  }
`;

const StyledImage = styled.img`
  max-width: 100%;
  height: auto;
  margin-bottom: 1.5rem;
  margin-top: 0.5rem;

  /* Mobile Portrait */
  @media ${device.mobilePortrait} {
    height: 5rem;
    width: auto;
  }

  /* Mobile Landscape and Tablets */
  @media ${device.mobileLandscape}, ${device.tablet} {
    height: 7rem;
    width: auto;
  }

  /* Desktop */
  @media ${device.laptop} {
    height: 8rem;
    width: auto;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  margin: 8px 0 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
  margin-top: 24px;
  flex-direction: row;
  justify-content: space-evenly;

  /* Reverse button order on mobile for better UX */
  @media ${device.mobilePortrait} {
    flex-direction: column-reverse;
    gap: 12px;
  }
`;

const CloseIcon = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  @media ${device.mobilePortrait} {
    top: 12px;
    right: 12px;
  }
`;

const MobileModal = ({
  open,
  handleClose,
  disableClose,
  title1,
  title2,
  imageSrc,
  primaryButtonTitle,
  primaryButtonOnClick,
  secondaryButtonTitle,
  secondaryButtonOnClick,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { isMobilePortrait } = useScreenSize();

  const handleCancel = () => {
    if (secondaryButtonOnClick) secondaryButtonOnClick();
    !disableClose && handleClose();
  };

  const handleConfirm = () => {
    if (primaryButtonOnClick) primaryButtonOnClick();
    !disableClose && handleClose();
  };

  return (
    <StyledModal
      open={open}
      onClose={disableClose ? undefined : handleClose}
      aria-labelledby="general-dialog-title"
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <ModalContainer
        style={{
          position: "relative",
        }}
      >
        {!disableClose && (
          <CloseIcon>
            <Cancel onClick={handleClose} />
          </CloseIcon>
        )}
        <StyledImage src={imageSrc ?? ASSETS.DialogImg} alt="Dialog Icon" />

        <ContentContainer>
          {title1 && (
            <TextView
              variant={isSmallScreen ? "body1" : "subtitle"}
              style={{
                fontWeight: 700,
                fontSize: isSmallScreen
                  ? "16px"
                  : isMediumScreen
                  ? "18px"
                  : "20px",
              }}
            >
              {title1}
            </TextView>
          )}
          {title2 && (
            <TextView
              variant={isSmallScreen ? "body2" : "body1"}
              style={{
                fontWeight: 500,
                fontSize: isSmallScreen ? "14px" : "16px",
                opacity: 0.9,
              }}
            >
              {title2}
            </TextView>
          )}
        </ContentContainer>

        <ButtonContainer>
          {secondaryButtonTitle && (
            <Button
              variant="outlined"
              onClick={handleCancel}
              fullWidth={true}
              sx={{
                borderColor: "#9B5AF8",
                height: isSmallScreen ? "42px" : "48px",
                color: "#9B5AF8",
                textTransform: "none",
                fontWeight: 500,
                fontSize: isSmallScreen ? "14px" : "16px",
                "&:hover": {
                  borderColor: "#7F47D8",
                  color: "#7F47D8",
                },
              }}
            >
              {secondaryButtonTitle}
            </Button>
          )}
          {primaryButtonTitle && (
            <Button
              variant="contained"
              onClick={handleConfirm}
              fullWidth={true}
              sx={{
                backgroundColor: "#9B5AF8",
                height: isSmallScreen ? "42px" : "48px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: isSmallScreen ? "14px" : "16px",
                "&:hover": {
                  backgroundColor: "#7F47D8",
                },
                color: "#FFFFFF",
              }}
            >
              {primaryButtonTitle}
            </Button>
          )}
        </ButtonContainer>
      </ModalContainer>
    </StyledModal>
  );
};

export default MobileModal;
